import { Check } from "lucide-react";
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type SelectHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";

type Option = { value: string; label: string; disabled: boolean; group: string | null };

/** As opções como estão no `<select>` agora (inclusive as de `<optgroup>`). */
function readOptions(select: HTMLSelectElement): Option[] {
  return Array.from(select.options).map(option => ({
    value: option.value,
    label: option.label || option.text,
    disabled: option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled),
    group: option.parentElement instanceof HTMLOptGroupElement ? option.parentElement.label : null
  }));
}

/** Troca o valor como o navegador trocaria: o React ouve o `change` e chama o
 *  `onChange` de quem usa o componente com `event.target.value` de sempre. */
function commit(select: HTMLSelectElement, value: string) {
  if (select.value === value) return;
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set;
  setter?.call(select, value);
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

type Placement = { top: number; left: number; width: number; maxHeight: number; up: boolean };

/**
 * O dropdown da plataforma (2026-10-15: "agora abre o do navegador… garantir
 * que esse modelo seja o padrão para todos os casos").
 *
 * É um `<select>` de verdade — mesmo `value`, mesmo `onChange(event)`, mesmas
 * `<option>`/`<optgroup>` como filhos, mesmo papel de `combobox` pro leitor de
 * tela e pros testes —, só que o menu nativo do navegador nunca abre: o clique
 * e as teclas que o abririam abrem a lista desenhada pela plataforma (o mesmo
 * visual da `ToolbarSelect`). Escolher na lista troca o valor do `<select>` e
 * dispara o `change`, então quem usa não muda nada além do nome da tag.
 *
 * A lista vai num portal com posição fixa: dentro de cartão com
 * `overflow: hidden` ela não é cortada, e abre pra cima quando falta espaço
 * embaixo. Teclado: Enter/Espaço/↑/↓ abrem; ↑/↓/Home/End andam; Enter
 * escolhe; Esc, Tab e clique fora fecham. Rolagem e redimensionamento fecham
 * (a lista ficaria solta da caixa).
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, onMouseDown, onKeyDown, children, ...props },
  forwardedRef
) {
  const selectRef = useRef<HTMLSelectElement>(null);
  useImperativeHandle(forwardedRef, () => selectRef.current as HTMLSelectElement);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) selectRef.current?.focus();
  }, []);

  const openList = () => {
    const select = selectRef.current;
    if (!select || select.disabled || props.multiple) return;
    const current = readOptions(select);
    if (current.length === 0) return;
    setOptions(current);
    const selected = current.findIndex(option => option.value === select.value);
    setActive(selected >= 0 ? selected : Math.max(0, current.findIndex(option => !option.disabled)));
    const rect = select.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const up = below < 220 && above > below;
    setPlacement({
      top: up ? rect.top - 6 : rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(160, Math.min(340, up ? above : below)),
      up
    });
    setOpen(true);
  };

  const choose = (option: Option) => {
    if (option.disabled || !selectRef.current) return;
    commit(selectRef.current, option.value);
    close();
  };

  const move = (from: number, step: 1 | -1) => {
    let next = from;
    for (let i = 0; i < options.length; i += 1) {
      next = Math.min(options.length - 1, Math.max(0, next + step));
      if (!options[next].disabled) return next;
    }
    return from;
  };

  const handleMouseDown = (event: MouseEvent<HTMLSelectElement>) => {
    onMouseDown?.(event);
    if (event.defaultPrevented || event.button !== 0 || props.multiple) return;
    event.preventDefault();
    selectRef.current?.focus();
    if (open) close();
    else openList();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSelectElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || props.multiple) return;
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key) || (event.altKey && event.key === "ArrowDown")) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(index => move(index, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(index => move(index, -1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(move(-1, 1));
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(move(options.length, -1));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (options[active]) choose(options[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  // Fecha no clique fora (a lista mora num portal, então "dentro" é a caixa
  // OU a lista), na rolagem e no redimensionamento.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current?.contains(target) || listRef.current?.contains(target)) return;
      close(false);
    };
    const onScroll = (event: Event) => {
      if (listRef.current && event.target instanceof Node && listRef.current.contains(event.target)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  // A opção ativa sempre à vista quando se anda pelo teclado.
  useLayoutEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    node?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const value = selectRef.current?.value;
  let lastGroup: string | null = null;

  return (
    <>
      <select
        {...props}
        ref={selectRef}
        className={`ui-select${className ? ` ${className}` : ""}${open ? " is-open" : ""}`}
        aria-expanded={props.multiple ? undefined : open}
        aria-controls={open ? listId : undefined}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
      >
        {children}
      </select>
      {open &&
        placement &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className={`toolbar-select-list ui-select-list${placement.up ? " opens-up" : ""}`}
            style={{
              top: placement.up ? undefined : placement.top,
              bottom: placement.up ? window.innerHeight - placement.top : undefined,
              left: placement.left,
              minWidth: placement.width,
              maxHeight: placement.maxHeight
            }}
          >
            {options.map((option, index) => {
              const header = option.group !== lastGroup && option.group !== null ? option.group : null;
              lastGroup = option.group;
              const selected = option.value === value;
              return (
                <li key={`${option.group ?? ""}:${option.value}:${index}`} role="presentation" className="ui-select-item">
                  {header && <span className="ui-select-group">{header}</span>}
                  <div
                    role="option"
                    data-index={index}
                    aria-selected={selected}
                    aria-disabled={option.disabled || undefined}
                    className={`toolbar-select-option${index === active ? " is-active" : ""}${selected ? " is-selected" : ""}${option.disabled ? " is-disabled" : ""}`}
                    onMouseEnter={() => !option.disabled && setActive(index)}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <span className="toolbar-select-option-label">{option.label}</span>
                    {selected && <Check size={15} aria-hidden="true" />}
                  </div>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
});
